import Icon from "./Icon";
import type { SeasonState } from "@/lib/store";

/**
 * Subtle chip showing a sister's current season (TTC / pregnant T2 wk 18 /
 * postpartum wk 6 / nursing). Drops into the /today header, /macros header,
 * and the coach roster row.
 */
export function seasonLabel(season: SeasonState | null): string | null {
  if (!season || season.kind === "none") return null;
  if (season.kind === "ttc") return "Trying to conceive";
  if (season.kind === "pregnant") {
    const weekHint = season.milestoneDate
      ? gestationalWeek(season.milestoneDate)
      : null;
    const t = season.trimester ? `T${season.trimester}` : "Pregnant";
    return weekHint ? `${t} \u00b7 wk ${weekHint}` : t;
  }
  if (season.kind === "postpartum") {
    const wk = season.weeksPostpartum ?? "early";
    return `Postpartum \u00b7 wk ${wk}`;
  }
  if (season.kind === "nursing") {
    return season.feeding === "exclusive" ? "Nursing exclusive" : "Nursing mixed";
  }
  return null;
}

/** Estimate gestational week from a due date (ISO yyyy-mm-dd). 40 weeks back. */
export function gestationalWeek(dueIso: string): number | null {
  const due = new Date(dueIso + "T00:00:00").getTime();
  if (Number.isNaN(due)) return null;
  const today = Date.now();
  const day = 1000 * 60 * 60 * 24;
  const daysUntil = Math.round((due - today) / day);
  const week = Math.max(1, Math.min(42, 40 - Math.round(daysUntil / 7)));
  return week;
}

const TONE: Record<NonNullable<SeasonState["kind"]>, { bg: string; fg: string; icon: string }> = {
  none:       { bg: "rgba(168,212,184,0.18)", fg: "var(--mnf-f)",      icon: "spa" },
  ttc:        { bg: "rgba(127,119,221,0.16)", fg: "var(--mnf-purple)", icon: "favorite" },
  pregnant:   { bg: "rgba(192,120,74,0.16)",  fg: "var(--mnf-t)",      icon: "child_friendly" },
  postpartum: { bg: "rgba(160,120,32,0.18)",  fg: "var(--mnf-gold)",   icon: "self_care" },
  nursing:    { bg: "rgba(168,212,184,0.22)", fg: "var(--mnf-f)",      icon: "water_drop" },
};

interface Props {
  season: SeasonState | null;
  size?: "sm" | "md";
  onClick?: () => void;
  /** Forest variant for use on dark headers. */
  variant?: "default" | "onForest";
  testId?: string;
}

export default function SeasonChip({ season, size = "md", onClick, variant = "default", testId }: Props) {
  const label = seasonLabel(season);
  if (!label || !season) return null;
  const tone = TONE[season.kind];
  const fontSize = size === "sm" ? 10.5 : 11.5;
  const padding = size === "sm" ? "3px 9px" : "5px 11px";
  const iconSize = size === "sm" ? 11 : 12;

  const palette = variant === "onForest"
    ? { bg: "rgba(245,240,232,0.16)", fg: "#F5F0E8" }
    : { bg: tone.bg, fg: tone.fg };

  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={onClick ? "hover-elevate" : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding,
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        fontSize,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        cursor: onClick ? "pointer" : "default",
        border: "none",
        fontFamily: "inherit",
      }}
    >
      <Icon name={tone.icon} size={iconSize} weight={500} />
      {label}
    </Tag>
  );
}
